
import { FareCategories, FareClasses, JourneyDirections, RailDayStart, GlobalModals } from '../constants/AppConstants';
import trainTime from '../lib/TrainTime';

import Fare from '../records/Fare';
import Fares from '../records/Fares';
import config from '../config';

import ModalActions from '../actions/ModalActions';

const shouldShowSeatfinderButton = trip => {
    if (!trip) {
        return false;
    }
    
    const outboundDateTime = trip.search.outboundDateTime.datetime;
    const durationHours = trainTime.calendarDuration(outboundDateTime, trainTime(new Date())).asHours();
    const configLimitDurationHours = config.get('seatingAvailabilityButtonShowDurationHours');

    return configLimitDurationHours >= durationHours;
};

const getCheapestFares = (trip, services, selectedService, fareClass, fareCategory, direction, size = 3) => {
    const relevantServices = services.getServicesForDirection(direction).slice(0, size);

    return relevantServices.map((service) => {
        if (service.isDisabledForTrip(trip)) {
            return new Fare();
        }

        const fare = fareCategory === FareCategories.OFF_PEAK
            ? getCheapestOffPeakFare(trip, service, selectedService, fareClass, fareCategory, direction)
            : getCheapestFare(trip, service, selectedService, fareClass, fareCategory, direction);

        if (!fare) {
            return new Fare();
        }

        return fare;
    });
};

const getCheapestFare = (trip, service, selectedService, fareClass, fareCategory, direction) => {
    const overAllCheapest = trip.getCheapestFareFromOverAll(fareCategory, fareClass, direction);
    if (direction === JourneyDirections.OUTWARD && overAllCheapest && service.isValidWithFare(overAllCheapest) && selectedService.isValidWithFare(overAllCheapest)) {
        return overAllCheapest;
    }

    let filters = [Fares.isSingle];
    if (!trip.isReturn()) {
        if (fareCategory === FareCategories.ADVANCE) {
            filters.push(Fares.isAdvance);
        }
        if (fareCategory === FareCategories.ANYTIME) {
            filters.push(Fares.isAnytime);
        }
    }
    if (fareClass === FareClasses.STANDARD) {
        filters.push(Fares.isStandard);
    } else {
        filters.push(Fares.isFirstClass);
    }

    return service.fares.getCheapestFare(filters);
};

const getCheapestOffPeakFare = (trip, service, selectedService, fareClass, fareCategory, direction) => {
    const overAllCheapest = trip.getCheapestFareFromOverAll(fareCategory, fareClass, direction);

    if (direction === JourneyDirections.OUTWARD
        && overAllCheapest
        && service.isValidWithFare(overAllCheapest)
        && selectedService.isValidWithFare(overAllCheapest)) {
        return overAllCheapest;
    }

    let filters = [Fares.isOffPeak];
    if (trip.isReturn() || trip.isOpenReturn()) {
        filters.push(Fares.isReturn);
    } else if (!trip.isReturn() && !trip.search.isReturnLondonTravelcard()) {
        filters.push(Fares.isSingle);
    } else if (!trip.isReturn() && trip.search.isReturnLondonTravelcard()) {
        filters.push(Fares.isSingleOrTravelcard);
    }

    if (fareClass === FareClasses.STANDARD) {
        filters.push(Fares.isStandard);
    } else {
        filters.push(Fares.isFirstClass);
    }

    if (direction === JourneyDirections.OUTWARD) {
        const result = service.fares.getCheapestFare(filters);
        return result;
    } else {
        const result = Fares.getCheapestCompatibleWithServiceFromList(
            getInwardServiceFareList(service, fareClass, Fares.isOffPeak),
            selectedService,
        );
        return result;
    }
};

const getInwardServiceFareList = (service, fareClass, filter) => {
    let fareList = service.fares.getReturnFares().filter(filter);

    if (fareClass === FareClasses.STANDARD) {
        fareList = fareList.filter(Fares.isStandard);
    }

    if (fareClass === FareClasses.FIRST_CLASS) {
        fareList = fareList.filter(Fares.isFirstClass);
    }

    return fareList;
};

const getCheapestAnytimeFare = (trip, service, selectedOutwardService, fareClass, fareCategory, direction) => {
    const overAllCheapest = trip.getCheapestFareFromOverAll(fareCategory, fareClass, direction);

    if (direction === JourneyDirections.OUTWARD
        && overAllCheapest
        && service.isValidWithFare(overAllCheapest)
        && selectedOutwardService.isValidWithFare(overAllCheapest)) {
        return overAllCheapest;
    }

    const result = Fares.getCheapestCompatibleWithServiceFromList(
        getInwardServiceFareList(service, fareClass, Fares.isAnytime),
        selectedOutwardService
    );

    return result;
};

const getJourneyDepartureDate = ({ services, search, direction }) => {
    const firstService = services && services.first();
    let time;

    if (!firstService) {
        time = direction === JourneyDirections.OUTWARD ?
            search.getOutboundDateTime() :
            search.getReturnDateTime();
    } else {
        time = firstService.getArrivalDateTime(false);
    }

    return time.format('ddd DD MMM YYYY');
};

const handleDifferentDaySearchResult = ({ search, response }) => {
    if (!response) {
        return;
    }

    const { result } = response;

    if (!result.outward.size) {
        return;
    }

    const searchTime = search.outboundDateTime.datetime.clone();
    const firstJourneyTime = result.outward.first().journey.origin.time.scheduledTime;
    const diffInDays = searchTime.startOf('day').diff(firstJourneyTime.clone().startOf('day'), 'days');

    if (!diffInDays) {
        return;
    }

    const nextRailDay = searchTime.clone().add(1, 'days').hours(RailDayStart.hours).minutes(RailDayStart.minutes).seconds(0);

    if (diffInDays === -1 && firstJourneyTime.isBefore(nextRailDay)) {
        return;
    }

    ModalActions.open(GlobalModals.INFO, {
        type: 'warning',
        heading: 'stationFinder.nextDay.modal.heading',
        message: 'stationFinder.nextDay.modal.message'
    });
};

export default {
    getCheapestFare,
    getCheapestOffPeakFare,
    getCheapestFares,
    getCheapestAnytimeFare,
    shouldShowSeatfinderButton,
    getJourneyDepartureDate,
    handleDifferentDaySearchResult
};



// WEBPACK FOOTER //
// ./src/utils/serviceUtils.js